import { ControlledDateInput } from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { MaskedInput } from "components/inputs";
import type { Control, FieldErrors } from "react-hook-form";
import type { CostProjectionDataType, MSADataType } from "types/DataTypes";

interface Props {
  allRows: Array<{
    legal_total_proposed_amount: number;
    legal_proposed_wcmsa_amount: number;
    legal_proposed_rx_amount: number;
    medical_total_proposed_amount: number;
    medical_proposed_wcmsa_amount: number;
    medical_proposed_rx_amount: number;
    is_excluded: boolean;
  }>;
  service: MSADataType | CostProjectionDataType;
  control: Control<MSADataType | CostProjectionDataType>;
  handleFieldChange: (field: string, value: unknown) => Promise<void>;
  reviewType: "MEDICAL" | "LEGAL";
  errors: FieldErrors<MSADataType | CostProjectionDataType>;
}

export const MedicalTreatmentCard = ({
  allRows,
  service,
  control,
  handleFieldChange,
  reviewType,
  errors,
}: Props) => {
  const includedRows = allRows.filter((row) => !row.is_excluded);
  const prefix = reviewType.toLowerCase() as "medical" | "legal";

  const totalProposedAmount =
    includedRows[0]?.[`${prefix}_total_proposed_amount`] || 0;
  const proposedWcmsaAmount =
    includedRows[0]?.[`${prefix}_proposed_wcmsa_amount`] || 0;
  const proposedRxAmount =
    includedRows[0]?.[`${prefix}_proposed_rx_amount`] || 0;

  // check if this is an MSA service (not cost projection, AR, ARS, DM)
  const isMSAService = service.type === "Medicare Set-Aside";

  return (
    <div>
      <FutureMedicalCard heading="Medical Treatment">
        <MaskedInput
          label="Total Proposed WCMSA Amount"
          name={`${prefix}_total_proposed_amount`}
          value={totalProposedAmount}
          mask="total_dollar"
          readOnly={true}
          errors={{}}
          onChange={() => {}}
        />

        <MaskedInput
          label="Proposed Medical WCMSA Amount"
          name={`${prefix}_proposed_wcmsa_amount`}
          value={proposedWcmsaAmount}
          mask="total_dollar"
          readOnly={true}
          errors={{}}
          onChange={() => {}}
        />

        <MaskedInput
          label="Proposed Rx WCMSA Amount"
          name={`${prefix}_proposed_rx_amount`}
          value={proposedRxAmount}
          mask="total_dollar"
          readOnly={true}
          errors={{}}
          onChange={() => {}}
        />

        {isMSAService ? (
          <>
            <ControlledDateInput
              control={control}
              name="most_recent_two_year_medicals_reviewed_from"
              label="Most recent two years of medical (From)"
              errors={errors}
              onSave={(value) =>
                handleFieldChange(
                  "most_recent_two_year_medicals_reviewed_from",
                  value,
                )
              }
            />

            <ControlledDateInput
              control={control}
              name="most_recent_two_year_medicals_reviewed_to"
              label="Most recent two years of medical (To)"
              errors={errors}
              onSave={(value) =>
                handleFieldChange(
                  "most_recent_two_year_medicals_reviewed_to",
                  value,
                )
              }
            />
          </>
        ) : (
          <ControlledDateInput
            control={control}
            name="recent_medical_reviewed_on"
            label="Recent Medical Record Reviewed"
            errors={errors}
            onSave={(value) =>
              handleFieldChange("recent_medical_reviewed_on", value)
            }
          />
        )}

        <ControlledDateInput
          control={control}
          name="payment_history_date"
          label="Claims Payment History Date"
          errors={errors}
          onSave={(value) => handleFieldChange("payment_history_date", value)}
        />

        <ControlledDateInput
          control={control}
          name="rx_history_date"
          label="Rx History Date"
          errors={errors}
          onSave={(value) => handleFieldChange("rx_history_date", value)}
        />
      </FutureMedicalCard>
    </div>
  );
};

export default MedicalTreatmentCard;
